import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/get-class-count-tuition-billing';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useUpdatePaymentsSettings } from '~entities/studios/api/update-payments-settings';

import { ClassCountChartsPanel } from '~features/payment/ui/class-count-tuition/class-count-charts-panel';

import { Button } from '~shared/shadcn/ui/button';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

const NONE_VALUE = '__none__';

export const ClassCountTuitionSettings = () => {
  const { data: studioDetails, isLoading: isStudioLoading } =
    useGetStudioDetails();
  const { data: charts = [] } = useGetClassCountTuitionBilling();
  const { mutateAsync: updateSettings, isPending: isSaving } =
    useUpdatePaymentsSettings();

  const [enabled, setEnabled] = useState(false);
  const [defaultChartId, setDefaultChartId] = useState<string>(NONE_VALUE);

  useEffect(() => {
    if (!studioDetails) return;
    setEnabled(!!studioDetails.familyClassCountTuitionEnabled);
    setDefaultChartId(
      studioDetails.defaultClassCountTuitionBillingId || NONE_VALUE,
    );
  }, [studioDetails]);

  const savePreferences = async (next: {
    enabled?: boolean;
    defaultChartId?: string;
  }) => {
    const nextEnabled = next.enabled ?? enabled;
    const nextDefault = next.defaultChartId ?? defaultChartId;
    try {
      await updateSettings({
        familyClassCountTuitionEnabled: nextEnabled,
        defaultClassCountTuitionBillingId:
          nextDefault === NONE_VALUE ? null : nextDefault,
      });
      toast.success('Family class-count preferences saved');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not save preferences',
      );
      setEnabled(!!studioDetails?.familyClassCountTuitionEnabled);
      setDefaultChartId(
        studioDetails?.defaultClassCountTuitionBillingId || NONE_VALUE,
      );
    }
  };

  return (
    <div>
      <div className='mb-6 border-b pb-4'>
        <Link className='flex w-fit items-center gap-2' to='/dashboard'>
          <CircleArrowLeft className='size-6 text-custom-secondary-1' />
          <h2 className='text-2xl font-semibold text-custom-secondary-1'>
            Family class-count tuition
          </h2>
        </Link>
        <p className='mt-2 max-w-2xl text-sm text-muted-foreground'>
          Price monthly classes from a family class-count table. One student in
          two classes and two siblings in one class each land on the same
          family total, then split equally.
        </p>
      </div>

      <div className='mx-auto max-w-4xl space-y-10'>
        <section className='rounded-xl border bg-white p-5'>
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='max-w-xl space-y-1'>
              <Label htmlFor='family-class-count-enabled' className='text-base'>
                Enable family class-count pricing
              </Label>
              <p className='text-sm text-muted-foreground'>
                When on, monthly enrollments that use a class-count chart (or
                the studio default) bill from the table instead of per-class
                tuition. Auto multi-class / multi-student discounts are skipped.
              </p>
            </div>
            {isStudioLoading ? (
              <Loader2 className='size-5 animate-spin text-muted-foreground' />
            ) : (
              <Switch
                id='family-class-count-enabled'
                checked={enabled}
                disabled={isSaving}
                onCheckedChange={(checked) => {
                  setEnabled(checked);
                  const autoDefault =
                    checked &&
                    defaultChartId === NONE_VALUE &&
                    charts.length > 0
                      ? charts[0]!._id
                      : undefined;
                  if (autoDefault) setDefaultChartId(autoDefault);
                  void savePreferences({
                    enabled: checked,
                    ...(autoDefault
                      ? { defaultChartId: autoDefault }
                      : {}),
                  });
                }}
              />
            )}
          </div>

          <div
            className={`mt-5 space-y-2 border-t pt-5 ${enabled ? '' : 'opacity-60'}`}
          >
            <Label>Studio default chart</Label>
            <p className='text-xs text-muted-foreground'>
              Used when a monthly class has no chart selected. The first chart
              you create (or enable with) becomes the default automatically.
            </p>
            <div className='flex flex-wrap items-center gap-3'>
              <Select
                value={defaultChartId}
                disabled={!enabled || isSaving}
                onValueChange={(value) => {
                  setDefaultChartId(value);
                  void savePreferences({ defaultChartId: value });
                }}
              >
                <SelectTrigger className='max-w-sm'>
                  <SelectValue placeholder='No default' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>No default</SelectItem>
                  {charts.map((chart) => (
                    <SelectItem key={chart._id} value={chart._id}>
                      {chart.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSaving && (
                <Button variant='ghost' size='sm' disabled>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving
                </Button>
              )}
            </div>
          </div>
        </section>

        <ClassCountChartsPanel />
      </div>
    </div>
  );
};
